import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import {
  arcPresence,
  ArcPresenceState,
  IArcPresenceEntry,
} from '../helpers/arcPresence/arcPresenceStore'

/** Working beats everything: a row Arc is on right now says so before it says what it did. */
const STATE_RANK: Record<ArcPresenceState, number> = {
  [ArcPresenceState.Working]: 3,
  [ArcPresenceState.NeedsApproval]: 2,
  [ArcPresenceState.Done]: 1,
  [ArcPresenceState.Failed]: 0,
}

const touchOf = (value: string): ArcPresenceState | null =>
  arcPresence
    .getSnapshot()
    .entries.filter(({ refs }) => refs.some((ref) => ref.value === value))
    .map(({ state }) => state)
    .sort((a, b) => STATE_RANK[b] - STATE_RANK[a])[0] ?? null

/**
 * What Arc is doing to the record this row shows, if anything. The row also
 * counts as on screen, so Arc's change to it is shown here and not as a chip.
 */
export const useArcTouch = (recordId: string | null | undefined): ArcPresenceState | null => {
  useEffect(() => (recordId ? arcPresence.watch(recordId) : undefined), [recordId])
  const getState = useCallback(() => (recordId ? touchOf(recordId) : null), [recordId])
  return useSyncExternalStore(arcPresence.subscribe, getState)
}

/** Holds a placeholder's activity on screen, so its result isn't also offered as a chip. */
export const useArcPlaceholder = (activityId: string): void => {
  useEffect(() => arcPresence.watch(activityId), [activityId])
}

const useArcEntries = (): IArcPresenceEntry[] =>
  useSyncExternalStore(arcPresence.subscribe, () => arcPresence.getSnapshot().entries)

/** The id of the record a finished create made, once its result names it. */
export const createdIdOf = ({ state, refs }: IArcPresenceEntry): string | null =>
  state === ArcPresenceState.Done ? (refs.find(({ key }) => key === 'id')?.value ?? null) : null

/**
 * Records Arc is making with `service` that belong in this list: shown as
 * placeholders while it works, and until the real row arrives to take over.
 */
export const useArcPendingCreates = (
  service: string,
  belongsHere: (entry: IArcPresenceEntry) => boolean,
): IArcPresenceEntry[] => {
  // The snapshot is replaced when a watched row appears, so this re-reads then too.
  const snapshot = useSyncExternalStore(arcPresence.subscribe, arcPresence.getSnapshot)
  return useMemo(
    () =>
      snapshot.entries.filter((entry) => {
        if (entry.service !== service || entry.state === ArcPresenceState.Failed) return false
        if (!belongsHere(entry)) return false
        const createdId = createdIdOf(entry)
        return !createdId || !arcPresence.isWatched(createdId)
      }),
    [snapshot, service, belongsHere],
  )
}

/** Calls `onDone` once for each write Arc finishes that this list should reload for. */
export const useArcFinishedWrites = (
  isRelevant: (entry: IArcPresenceEntry) => boolean,
  onDone: () => void,
): void => {
  const entries = useArcEntries()
  const handled = useRef(new Set<string>())
  useEffect(() => {
    const finished = entries.filter(
      (entry) =>
        entry.isWrite &&
        entry.state === ArcPresenceState.Done &&
        !handled.current.has(entry.id) &&
        isRelevant(entry),
    )
    if (finished.length === 0) return
    finished.forEach(({ id }) => handled.current.add(id))
    onDone()
  }, [entries, isRelevant, onDone])
}

const IN_FLIGHT = new Set([ArcPresenceState.Working, ArcPresenceState.NeedsApproval])
/** The rail shows the latest few, like the call's agenda and the done notes beside them. */
export const IN_FLIGHT_MAX = 3

/**
 * Changes Arc is making, or waiting on an OK to make, that no row on screen shows:
 * from a chat, an automation, anywhere. Turns `isShownElsewhere` claims have their own surface.
 */
export const useArcOffScreenWrites = (
  isShownElsewhere: (turnId: string) => boolean,
): IArcPresenceEntry[] => {
  // The snapshot is replaced when a watched row appears, so this re-reads then too.
  const snapshot = useSyncExternalStore(arcPresence.subscribe, arcPresence.getSnapshot)
  return useMemo(
    () =>
      snapshot.entries
        .filter(
          (entry) =>
            entry.isWrite &&
            IN_FLIGHT.has(entry.state) &&
            !isShownElsewhere(entry.chatHistoryId) &&
            !arcPresence.isOnScreen(entry),
        )
        .slice(-IN_FLIGHT_MAX),
    [snapshot, isShownElsewhere],
  )
}

export const useArcElsewhere = () =>
  useSyncExternalStore(arcPresence.subscribe, () => arcPresence.getSnapshot().elsewhere)

export const useIsArcWorking = (): boolean =>
  useSyncExternalStore(arcPresence.subscribe, () =>
    arcPresence.getSnapshot().entries.some(({ state }) => state === ArcPresenceState.Working),
  )

export const useArcNavigationRequest = () =>
  useSyncExternalStore(arcPresence.subscribe, () => arcPresence.getSnapshot().navigation)
